import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';

import { GpsApi } from '../../api/gps.api';
import { timeDelay } from '../../helpers/time.helpers';
import { BrowseFilter } from '../../models/filter';
import { GpsTrack } from '../../models/gps-track.interface';
import { BrowseSorting } from '../../models/sorting';
import { Sport } from '../../models/sport';
import { Store } from '../../../../shared/store';
import { GpsState } from './gps.state';
import { GpsOnly } from '@kinomap-dev/ng-ui-lib';

const gpsState: GpsState = new GpsState();

@Injectable({ providedIn: 'root' })
export class GpsStore extends Store<GpsState> {
  gpsTracks$ = this.state$.pipe(map((s) => s.gpsTracks));
  isLoadingGpsTracks$ = this.state$.pipe(map((s) => s.isLoadingGpsTracks));
  areGpsTracksComplete$ = this.state$.pipe(map((s) => s.areGpsTracksComplete));

  constructor(private gpsApi: GpsApi) {
    super(gpsState);
  }

  // SET GPS TRACKS

  setGpsTracks(nextGpsTracks: GpsOnly[]): void {
    this.setState({ ...this.state, gpsTracks: nextGpsTracks });
  }

  setIsLoadingGpsTracks(nextIsLoadingGpsTracks: boolean): void {
    this.setState({
      ...this.state,
      isLoadingGpsTracks: nextIsLoadingGpsTracks,
    });
  }

  setAreGpsTracksComplete(nextAreGpsTracksComplete: boolean): void {
    this.setState({
      ...this.state,
      areGpsTracksComplete: nextAreGpsTracksComplete,
    });
  }

  // GET GPS TRACKS FROM API

  async getGpsTracks(
    {
      newSelectedSport,
      newFilters,
      newSorting,
      nextPage,
    }: {
      newSelectedSport?: Sport;
      newFilters?: BrowseFilter[];
      newSorting?: BrowseSorting;
      nextPage: boolean;
    } = { nextPage: false }
  ): Promise<void> {
    if (nextPage && this.state.areGpsTracksComplete) {
      return;
    }

    let goOn: boolean = !this.state.isLoadingGpsTracks;
    while (!goOn) {
      await timeDelay(100);
      goOn = !this.state.isLoadingGpsTracks;
    }

    if (!nextPage) {
      this.setGpsTracks([]);
      this.setAreGpsTracksComplete(false);
    }

    this.setIsLoadingGpsTracks(true);

    try {
      const fetchedGpsTracks: GpsOnly[] = await firstValueFrom(
        this.gpsApi.getGpsTracks$(
          newSelectedSport,
          newFilters,
          newSorting,
          nextPage
        )
      );
      const newGpsTracks: GpsOnly[] =
        this.getNewGpsTracksWithoutDuplicate(fetchedGpsTracks);
      this.setGpsTracks(newGpsTracks);

      this.setAreGpsTracksComplete(this.gpsApi.isLastPageLoaded);
    } catch (err) {
      this.setAreGpsTracksComplete(true);
    } finally {
      this.setIsLoadingGpsTracks(false);
    }
  }

  private getNewGpsTracksWithoutDuplicate(
    fetchedGpsTracks: GpsOnly[]
  ): GpsOnly[] {
    const newGpsTracksWithDuplicates: GpsOnly[] = [
      ...this.state.gpsTracks,
      ...fetchedGpsTracks,
    ];
    const obj: {} = {};
    newGpsTracksWithDuplicates.forEach(
      (content) => (obj[content.id] = content)
    );
    return [
      ...new Set(newGpsTracksWithDuplicates.map((track) => track.id)),
    ].map((id) => obj[id]);
  }
}
